// Transformer interface for bidirectional unit conversion in the Apollo link chain.
// parseValue: converts server units (imperial) → user-preferred units on response
// serialize: converts user-preferred units → server units on request (currently identity for most)
export interface Transformer {
  parseValue: (v: unknown) => unknown;
  serialize: (v: unknown) => unknown;
}

// Keyed by GraphQL __typename → field name → Transformer
export type Transformers = Record<string, Record<string, Transformer>>;

import { UnitsConverter, USER_UNITS_PREF_KEY } from '../../util';
import { MassUnitType, UserUnitsPrefFragmentFragment } from './__generated';

// Check localStorage for user's mass unit preference — used by MassTransformer
// to conditionally convert lbs→kg only when the user has opted into metric mass
export function hasUserSelectedKgUnit() {
  const userUnitsPrefVal = localStorage.getItem(USER_UNITS_PREF_KEY);
  const localeUnitSystem: UserUnitsPrefFragmentFragment | null = userUnitsPrefVal
    ? (JSON.parse(userUnitsPrefVal) as UserUnitsPrefFragmentFragment)
    : null;
  return localeUnitSystem && localeUnitSystem.mass_unit === MassUnitType.Kilogram;
}

// Server sends epoch millis; parseValue converts to JS Date for component use
const DateTransformer: Transformer = {
  parseValue: (value?: number | null) =>
    value === undefined || value === null ? value : new Date(value),
  serialize: (value?: Date | null) =>
    value === undefined || value === null ? value : value.getTime(),
};

// Server stores distances in feet; convert to meters for metric-locale users
const MetersTransformer: Transformer = {
  parseValue: (value?: number | null) => {
    return value === undefined || value === null
      ? value
      : UnitsConverter.convertFeetToMeters(value);
  },
  serialize: (value?: number | null) => value,
};

// Server stores small distances (e.g. cable diameter) in inches; convert to cm for metric users
const InchesToCentimetersTransformer: Transformer = {
  parseValue: (value?: number | null) => {
    return value === undefined || value === null
      ? value
      : UnitsConverter.convertInchesToCentimeters(value);
  },
  serialize: (value?: number | null) => value,
};

// Server stores temperatures in Fahrenheit; convert to Celsius for metric users
const CelsiusTransformer: Transformer = {
  parseValue: (value?: number | null) => {
    return value === undefined || value === null
      ? value
      : UnitsConverter.convertFahrenheitToCelsius(value);
  },
  serialize: (value?: number | null) => value,
};

// Server stores mass in pounds; conditionally convert to kg based on user preference
const MassTransformer: Transformer = {
  parseValue: (value?: number | null) => {
    return value === undefined || value === null
      ? value
      : hasUserSelectedKgUnit()
        ? UnitsConverter.convertPoundToKilogram(value)
        : value;
  },
  serialize: (value?: number | null) => value,
};

// Server stores test weight in lbs/bu; convert to kg/hL for metric users
const KgPerHectoliterTransformer: Transformer = {
  parseValue: (value?: number | null) => {
    return value === undefined || value === null
      ? value
      : UnitsConverter.convertPoundPerBuToKilogramPerHectoliter(value);
  },
  serialize: (value?: number | null) => value,
};

// Server stores volume in bushels; convert to metric tons for metric users
const MetricTonTransformer: Transformer = {
  parseValue: (value?: number | null) => {
    return value === undefined || value === null
      ? value
      : UnitsConverter.convertBuToMetricTon(value);
  },
  serialize: (value?: number | null) => value,
};

export {
  DateTransformer,
  MetersTransformer,
  CelsiusTransformer,
  MassTransformer,
  KgPerHectoliterTransformer,
  MetricTonTransformer,
  InchesToCentimetersTransformer,
};
